import type { Filter } from '@cvent/venue-search-shared/types';
import type { BrandAndChainResponse, Option } from '@/app/api/venue-autocomplete/route';
import React, { useCallback, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';

const CityMultiSelect = dynamic(() => import('@/components/filters/city/CityMultiSelect'), { ssr: false });

type Props = {
  options: BrandAndChainResponse['cityOptions'];
  selected: Filter;
  setSelected: (selections: Filter) => void;
  testID?: string;
};

export default function CityFilter({ options, selected, setSelected, testID }: Props) {
  const { title, clear, noMatchFound, optionsTitle } = options.labels;
  const [disableClear, setDisableClear] = useState<boolean>(true);

  useMemo(() => {
    // Clear button enable/disable logic
    const found = selected.CITY && selected.CITY.length > 0;
    if (found) {
      setDisableClear(false);
    }
  }, [selected.CITY]);

  // Clear button click handler
  const clearOnClick = useCallback(() => {
    const clearedValues: Option[] = [];
    setSelected({
      ...selected,
      CITY: clearedValues
    });
    setDisableClear(true);
  }, [selected, setSelected]);

  const onUpdate = useCallback(
    (selections: string[]) => {
      if (selections.length === 0) setDisableClear(true);
      const selectedCities: Option[] =
        selections.length > 0 ? options.completeOptions.filter(o => selections.includes(o.value)) : [];
      setSelected({
        ...selected,
        CITY: selectedCities
      });
    },
    [options.completeOptions, selected, setSelected]
  );

  return (
    <div data-cvent-id={`${testID}-cities`} className="my-4 rounded-xl border border-neutral-20 p-2">
      <div className="leading-5 mb-3 ml-1.5 mt-1 flex text-b-md font-normal">{title}</div>
      <CityMultiSelect
        clear={{ label: clear, disable: disableClear, onClick: clearOnClick }}
        labels={{ noMatchFound, optionsTitle, title }}
        locale={options.locale}
        defaultOption={options.suggestedOptions}
        completeOption={options.completeOptions}
        onUpdate={onUpdate}
        selected={selected}
      />
    </div>
  );
}
